/**
 * DSD-DSD 대사 결과 HTML 다운로드
 *
 * DOM clone + CSS 추출 + embedded JS 방식.
 *
 * 사이드바, 필터 탭, 셀/문단/span 클릭, change navigator 모두 동작하는 완전한 복제본.
 */

import type {
  DsdDsdCompareItem,
  DsdDsdCompareResponse,
} from "@/apis/dsd-dsd-compare/types";
import {
  buildChangeList,
  type CellChangeEntry,
  type ChangeEntry,
  type ParagraphChangeEntry,
  type SpanChangeEntry,
} from "./change-navigator";
import type { DsdDsdViewMode } from "./compare-header";
import { downloadBlob } from "@/utils/download-blob";

// ============================================
// Font
// ============================================

const FONT_IMPORT = `@import url("https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/variable/pretendardvariable.min.css");`;

// ============================================
// Inline Script (vanilla JS)
// ============================================

const INLINE_SCRIPT = `
(function() {
  // ═══════════════════════════════════════════
  // Utilities
  // ═══════════════════════════════════════════

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  function escapeDlAttr(id) {
    if (typeof CSS !== "undefined" && CSS.escape) return CSS.escape(id);
    return String(id).replace(/\\\\/g, "\\\\\\\\").replace(/"/g, '\\\\"');
  }
  function cellIdSelector(id) {
    return '[data-cell-id="' + escapeDlAttr(id) + '"]';
  }

  // ═══════════════════════════════════════════
  // Highlight / Selection
  // ═══════════════════════════════════════════

  var DL_FOCUSED = "dl-focused";
  var DL_RING = "dl-ring";
  var DL_RING_RED = "dl-ring-red";
  var DL_RING_LIME = "dl-ring-lime";
  var DL_HIGHLIGHTED = "dl-highlighted";
  var DL_SEL_ACTIVE = "dl-sel-active";
  var DL_SUM_MATCH_SEL = "dl-sum-match-sel";

  var CELL_DATA = window.__DL_CELL_DATA__ || {};
  var CELL_PAIR_MAP = window.__DL_CELL_PAIR_MAP__ || {};

  var SELECTION_COLORS = {
    primary: { border: "#171717", bg: "#171717" },
    red: { border: "#dc2626", bg: "#dc2626" },
    lime: { border: "#65a30d", bg: "#65a30d" },
    indigo: { border: "#4f46e5", bg: "#4f46e5" },
    orange: { border: "#f97316", bg: "#f97316" },
    slate: { border: "#64748b", bg: "#64748b" }
  };
  var DEFAULT_PLAIN_BORDER = "#171717";

  function resolveSelectionInfo(m, d) {
    if (!d) return null;
    if (m === "changes") {
      if (d.status === "deleted") return { label: "삭제", color: "red" };
      if (d.status === "inserted") return { label: "추가", color: "lime" };
      if (d.status === "modified") return { label: "변경", color: "indigo" };
      return null;
    }
    if (d.sumCaution) return { label: "합계 불일치", color: "slate" };
    if (d.sumRole === "parent") return { label: "합계", color: "orange" };
    if (m === "sum_diffs") return null;
    if (d.status === "deleted") return { label: "삭제", color: "red" };
    if (d.status === "inserted") return { label: "추가", color: "lime" };
    if (d.status === "modified") return { label: "변경", color: "indigo" };
    return null;
  }

  function addSelectionOverlay(cellEl, color, label) {
    var c = SELECTION_COLORS[color];
    if (!c) return;
    var border = document.createElement("div");
    border.className = "dl-sel-border";
    border.style.cssText = "position:absolute;inset:0;pointer-events:none;border:2px solid " + c.border + ";z-index:10;";
    cellEl.appendChild(border);
    var tab = document.createElement("div");
    tab.className = "dl-sel-tab";
    tab.style.cssText = "position:absolute;bottom:100%;left:0;z-index:10;padding:1px 4px;border-radius:4px 4px 0 0;font-size:0.6875rem;font-weight:600;line-height:1.2;color:#fff;white-space:nowrap;background:" + c.bg + ";";
    tab.textContent = label;
    cellEl.appendChild(tab);
    cellEl.style.overflow = "visible";
  }

  function addPlainBorder(cellEl, borderColor) {
    var border = document.createElement("div");
    border.className = "dl-plain-border";
    border.style.cssText = "position:absolute;inset:0;pointer-events:none;border:2px solid " + (borderColor || DEFAULT_PLAIN_BORDER) + ";z-index:10;";
    cellEl.appendChild(border);
  }

  function clearHighlights() {
    qsa(".dl-sel-border").forEach(function(el) { el.remove(); });
    qsa(".dl-sel-tab").forEach(function(el) { el.remove(); });
    qsa(".dl-plain-border").forEach(function(el) { el.remove(); });
    qsa("." + DL_SEL_ACTIVE).forEach(function(el) {
      el.classList.remove(DL_SEL_ACTIVE);
      el.style.overflow = "";
    });
    qsa("." + DL_SUM_MATCH_SEL).forEach(function(el) { el.classList.remove(DL_SUM_MATCH_SEL); });
    qsa("." + DL_FOCUSED).forEach(function(el) { el.classList.remove(DL_FOCUSED); });
    qsa("." + DL_RING).forEach(function(el) { el.classList.remove(DL_RING); });
    qsa("." + DL_RING_RED).forEach(function(el) { el.classList.remove(DL_RING_RED); });
    qsa("." + DL_RING_LIME).forEach(function(el) { el.classList.remove(DL_RING_LIME); });
    qsa("." + DL_HIGHLIGHTED).forEach(function(el) { el.classList.remove(DL_HIGHLIGHTED); });
  }

  function _highlightChildrenOf(cellId, data) {
    if (!data || !data.sumChildren || data.sumChildren.length === 0) return;
    var u = cellId.lastIndexOf("_");
    var prefix = u >= 0 ? cellId.substring(0, u + 1) : "";
    data.sumChildren.forEach(function(addr) {
      var el = qs(cellIdSelector(prefix + addr));
      if (el) el.classList.add(DL_HIGHLIGHTED);
    });
  }

  function highlightSumChildren(cellId) {
    if (mode === "changes") return;
    _highlightChildrenOf(cellId, CELL_DATA[cellId]);
    var pairId = CELL_PAIR_MAP[cellId];
    if (pairId) _highlightChildrenOf(pairId, CELL_DATA[pairId]);
  }

  // ═══════════════════════════════════════════
  // Filter tabs (전체 / 합계 불일치 / 변경 사항)
  // ═══════════════════════════════════════════

  var mode = window.__DL_INITIAL_MODE__ || "default";

  function isSumDiffsActive(m) { return m === "sum_diffs" || m === "sum_diffs_and_changes"; }
  function isChangesActive(m) { return m === "changes" || m === "sum_diffs_and_changes"; }

  function applyFilter() {
    // 아이템 show/hide
    qsa("[data-dl-item]").forEach(function(el) {
      var hasChanges = el.getAttribute("data-dl-has-changes") === "true";
      var hasSumCaution = el.getAttribute("data-dl-has-sum-caution") === "true";
      var wantChanges = isChangesActive(mode);
      var wantSum = isSumDiffsActive(mode);

      if (mode === "default") {
        el.style.display = "";
      } else if (wantChanges && wantSum) {
        el.style.display = (hasChanges || hasSumCaution) ? "" : "none";
      } else if (wantChanges) {
        el.style.display = hasChanges ? "" : "none";
      } else {
        el.style.display = hasSumCaution ? "" : "none";
      }
    });

    // 섹션 그룹: 모든 아이템이 숨겨지면 섹션도 숨김
    qsa("[data-dl-section-group]").forEach(function(section) {
      var visible = qsa("[data-dl-item]", section).some(function(el) {
        return el.style.display !== "none";
      });
      section.style.display = visible ? "" : "none";
    });

    // 사이드바 섹션 버튼 연동 (sidebarBtns는 아래 sidebar 섹션에서 초기화)
    if (typeof sidebarBtns !== "undefined") {
      sidebarBtns.forEach(function(item) {
        var sectionGroup = document.getElementById("section-" + item.sectionId);
        if (sectionGroup) {
          item.btn.style.display = (sectionGroup.style.display === "none") ? "none" : "";
        }
      });
    }

    // 모드별 body class → CSS로 셀 배경색/칩 제어
    document.body.className = document.body.className.replace(/dl-mode-\\S+/g, "");
    document.body.classList.add("dl-mode-" + mode);

    // 모드 전환 시 선택/하이라이트 초기화 (선택 색상이 모드에 따라 달라짐)
    clearHighlights();

    // 네비게이터 엔트리 재구축 + 셀 하이라이트 재적용
    var prevNavIdx = currentNavIndex;
    rebuildFilteredEntries();
    if (prevNavIdx >= 0) {
      if (prevNavIdx >= FILTERED_ENTRIES.length) {
        hideNavigator();
      } else {
        navigateTo(prevNavIdx);
      }
    }

    updateTabStyles();
  }

  // 탭 스타일
  var ACTIVE_CLASSES = ["bg-primary-base", "text-static-white"];
  var INACTIVE_CLASSES = ["border", "border-line-normal", "text-label-normal"];

  function setTabActive(btn) {
    INACTIVE_CLASSES.forEach(function(c) { btn.classList.remove(c); });
    ACTIVE_CLASSES.forEach(function(c) { btn.classList.add(c); });
  }
  function setTabInactive(btn) {
    ACTIVE_CLASSES.forEach(function(c) { btn.classList.remove(c); });
    INACTIVE_CLASSES.forEach(function(c) { btn.classList.add(c); });
  }

  function updateTabStyles() {
    qsa("[data-dl-filter]").forEach(function(btn) {
      var tab = btn.getAttribute("data-dl-filter");
      var isActive = false;
      if (tab === "default") isActive = (mode === "default");
      else if (tab === "sum_diffs") isActive = isSumDiffsActive(mode);
      else if (tab === "changes") isActive = isChangesActive(mode);
      if (isActive) setTabActive(btn); else setTabInactive(btn);
    });
  }

  qsa("[data-dl-filter]").forEach(function(btn) {
    btn.addEventListener("click", function() {
      var tab = btn.getAttribute("data-dl-filter");
      if (tab === "default") {
        mode = "default";
      } else if (tab === "sum_diffs") {
        if (isSumDiffsActive(mode)) {
          mode = isChangesActive(mode) ? "changes" : "default";
        } else {
          mode = isChangesActive(mode) ? "sum_diffs_and_changes" : "sum_diffs";
        }
      } else if (tab === "changes") {
        if (isChangesActive(mode)) {
          mode = isSumDiffsActive(mode) ? "sum_diffs" : "default";
        } else {
          mode = isSumDiffsActive(mode) ? "sum_diffs_and_changes" : "changes";
        }
      }
      applyFilter();
    });
  });

  // ═══════════════════════════════════════════
  // Sidebar — 완전 자립형 (data attribute 의존 없음)
  // DOM에서 직접 section id와 버튼 텍스트를 매칭
  // ═══════════════════════════════════════════

  var ACTIVE_CLASS = "dl-sidebar-active";

  // 1) section-* ID를 가진 모든 요소 수집
  var sectionIds = [];
  qsa("[id^='section-']").forEach(function(el) {
    sectionIds.push(el.id.replace("section-", ""));
  });
  var sectionIdSet = {};
  sectionIds.forEach(function(id) { sectionIdSet[id] = true; });

  // 2) 사이드바 내부 버튼 중 텍스트가 sectionId와 일치하는 것 찾기
  var sidebarEl = qs("[data-sidebar='sidebar']");
  var sidebarBtns = []; // { btn, sectionId }
  var sectionToBtn = {};
  if (sidebarEl) {
    qsa("button", sidebarEl).forEach(function(btn) {
      // 첫 번째 자식 span의 텍스트 사용 (Badge 등 다른 자식 제외)
      var span = btn.querySelector("span");
      var text = span ? span.textContent.trim() : btn.textContent.trim();
      if (sectionIdSet[text]) {
        sidebarBtns.push({ btn: btn, sectionId: text });
        sectionToBtn[text] = btn;
      }
    });
  }

  // 3) 스크롤 컨테이너 찾기: section-* 첫 요소에서 부모를 타고 올라감
  var scrollContainer = null;
  if (sectionIds.length > 0) {
    var firstSection = document.getElementById("section-" + sectionIds[0]);
    if (firstSection) {
      var p = firstSection.parentElement;
      while (p && p !== document.body && p !== document.documentElement) {
        var style = getComputedStyle(p);
        if (
          (style.overflowY === "auto" || style.overflowY === "scroll") &&
          p.scrollHeight > p.clientHeight
        ) {
          scrollContainer = p;
          break;
        }
        p = p.parentElement;
      }
    }
  }

  // 3b) 셀 선택/네비 시 세로 + TableHorizontalNavigator 등 중첩 가로 스크롤
  var HORIZONTAL_SCROLL_MARGIN_PX = 12;

  function getCompareScrollRoot() {
    return scrollContainer || qs("[data-scroll-root]") || document.body;
  }

  function getDomDepth(node, root) {
    var depth = 0;
    var n = node;
    while (n) {
      if (n === root) return depth;
      depth++;
      n = n.parentElement;
    }
    return depth;
  }

  function isHorizontallyScrollable(el) {
    var st = getComputedStyle(el);
    var ox = st.overflowX;
    if (ox !== "auto" && ox !== "scroll") return false;
    return el.scrollWidth > el.clientWidth + 1;
  }

  function collectHorizontalScrollersForElement(root, el) {
    var list = [];
    var p = el.parentElement;
    while (p && p !== root) {
      if (isHorizontallyScrollable(p)) list.push(p);
      p = p.parentElement;
    }
    return list;
  }

  function alignHorizontalScrollerToElements(scroller, elements, margin, behavior) {
    var visible = elements.filter(function(e) { return scroller.contains(e); });
    if (visible.length === 0) return;
    var sRect = scroller.getBoundingClientRect();
    var minL = Infinity;
    var maxR = -Infinity;
    for (var vi = 0; vi < visible.length; vi++) {
      var r = visible[vi].getBoundingClientRect();
      minL = Math.min(minL, r.left);
      maxR = Math.max(maxR, r.right);
    }
    if (minL === Infinity) return;
    var delta = 0;
    if (minL < sRect.left + margin) {
      delta = minL - sRect.left - margin;
    } else if (maxR > sRect.right - margin) {
      delta = maxR - sRect.right + margin;
    }
    if (delta !== 0) {
      scroller.scrollBy({ left: delta, behavior: behavior });
    }
  }

  function scrollCompareCellTargetsIntoView(cellIds, behavior) {
    behavior = behavior || "smooth";
    var root = getCompareScrollRoot();
    var seen = {};
    var uniqueIds = [];
    for (var ui = 0; ui < cellIds.length; ui++) {
      var cid = cellIds[ui];
      if (!cid || seen[cid]) continue;
      seen[cid] = true;
      uniqueIds.push(cid);
    }
    var elements = [];
    for (var ej = 0; ej < uniqueIds.length; ej++) {
      var cel = qs(cellIdSelector(uniqueIds[ej]));
      if (cel) elements.push(cel);
    }
    if (elements.length === 0) return;

    elements[0].scrollIntoView({
      behavior: behavior,
      block: "center",
      inline: "nearest"
    });

    requestAnimationFrame(function() {
      var ordered = [];
      for (var ok = 0; ok < elements.length; ok++) {
        var sList = collectHorizontalScrollersForElement(root, elements[ok]);
        for (var si = 0; si < sList.length; si++) {
          var sc = sList[si];
          if (ordered.indexOf(sc) < 0) ordered.push(sc);
        }
      }
      ordered.sort(function(a, b) {
        return getDomDepth(b, root) - getDomDepth(a, root);
      });
      for (var oi = 0; oi < ordered.length; oi++) {
        alignHorizontalScrollerToElements(
          ordered[oi],
          elements,
          HORIZONTAL_SCROLL_MARGIN_PX,
          "auto"
        );
      }
    });
  }

  // 4) active 상태 업데이트 (스크롤 위치 기반)
  function updateActiveSidebar() {
    if (sidebarBtns.length === 0) return;

    // scrollContainer 내의 상대 위치로 판정
    var containerRect = scrollContainer
      ? scrollContainer.getBoundingClientRect()
      : { top: 0 };
    var threshold = containerRect.top + 80; // 컨테이너 상단 + 약간 여유

    var activeId = sidebarBtns[0].sectionId; // 기본: 첫 섹션
    for (var i = 0; i < sidebarBtns.length; i++) {
      var el = document.getElementById("section-" + sidebarBtns[i].sectionId);
      if (el && el.getBoundingClientRect().top <= threshold) {
        activeId = sidebarBtns[i].sectionId;
      }
    }

    // 모든 버튼에서 active 제거, 해당 버튼에 active 추가
    sidebarBtns.forEach(function(item) { item.btn.classList.remove(ACTIVE_CLASS); });
    if (sectionToBtn[activeId]) {
      sectionToBtn[activeId].classList.add(ACTIVE_CLASS);
      sectionToBtn[activeId].scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }

  // 5) 스크롤 이벤트 리스너
  if (scrollContainer) {
    scrollContainer.addEventListener("scroll", updateActiveSidebar, { passive: true });
  }
  // fallback: window scroll도 감시
  window.addEventListener("scroll", updateActiveSidebar, { passive: true });
  // 초기 상태 즉시 적용
  updateActiveSidebar();

  // 6) 사이드바 버튼 클릭 → 해당 섹션으로 스크롤
  sidebarBtns.forEach(function(item) {
    item.btn.addEventListener("click", function() {
      var target = document.getElementById("section-" + item.sectionId);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
        // scrollIntoView 완료 후 active 업데이트
        setTimeout(updateActiveSidebar, 500);
      }
    });
  });

  // 7) Sidebar expand/collapse 토글
  // 아이콘만 있고 텍스트 없는 버튼 = IconButton (PanelLeft/PanelLeftOpen)
  if (sidebarEl) {
    qsa("button", sidebarEl).forEach(function(btn) {
      // sectionToBtn에 없고, 텍스트가 비어있으면 토글 버튼
      var text = btn.textContent.trim();
      if (text === "" && btn.querySelector("svg")) {
        btn.addEventListener("click", function() {
          var wrapper = qs("[data-state][data-collapsible]");
          if (!wrapper) return;
          var isExpanded = wrapper.getAttribute("data-state") === "expanded";
          wrapper.setAttribute("data-state", isExpanded ? "collapsed" : "expanded");
          wrapper.setAttribute("data-collapsible", isExpanded ? "icon" : "");
        });
      }
    });
  }

  // ═══════════════════════════════════════════
  // Change Navigator (모달)
  // ═══════════════════════════════════════════

  var ALL_ENTRIES = window.__DL_CHANGE_ENTRIES__ || [];
  var FILTERED_ENTRIES = [];
  var cellIdToIdx = {};
  var paraIdToIdx = {};
  var spanIdToIdx = {};

  function isEntryRelevantForMode(entry, m) {
    if (m === "default") return true;
    if (entry.type === "paragraph" || entry.type === "span") {
      return isChangesActive(m);
    }
    var wantChanges = isChangesActive(m);
    var wantSum = isSumDiffsActive(m);
    var hasChange = entry.status === "modified" || entry.status === "deleted" || entry.status === "inserted";
    var hasSumCaution = entry.sumCaution;
    if (wantChanges && wantSum) return hasChange || hasSumCaution;
    if (wantChanges) return hasChange;
    if (wantSum) return hasSumCaution;
    return true;
  }

  function rebuildFilteredEntries() {
    FILTERED_ENTRIES = [];
    cellIdToIdx = {};
    paraIdToIdx = {};
    spanIdToIdx = {};
    ALL_ENTRIES.forEach(function(entry) {
      if (!isEntryRelevantForMode(entry, mode)) return;
      var idx = FILTERED_ENTRIES.length;
      FILTERED_ENTRIES.push(entry);
      if (entry.type === "cell") {
        entry.cellIds.forEach(function(id) { cellIdToIdx[id] = idx; });
      } else if (entry.type === "paragraph") {
        paraIdToIdx[entry.paragraphId] = idx;
      } else if (entry.type === "span") {
        spanIdToIdx[entry.spanChangeId] = idx;
      }
    });
  }
  rebuildFilteredEntries();

  var modalContainer = document.createElement("div");
  modalContainer.id = "dl-navigator";
  modalContainer.style.cssText = "position:fixed;bottom:24px;right:24px;z-index:50;display:none;";
  document.body.appendChild(modalContainer);

  var currentNavIndex = -1;
  var activeNavCellId = null;

  // Chip config (새 디자인)
  var chipCfg = {
    sumMismatch: { label: "합계 불일치", bg: "#171717", color: "#fff",
      icon: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>' },
    modified: { label: "수정", bg: "#6366f1", color: "#fff",
      icon: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6"/><path d="M12 18h.01"/></svg>' },
    deleted: { label: "삭제", bg: "#ef4444", color: "#fff",
      icon: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></svg>' },
    inserted: { label: "추가", bg: "#84cc16", color: "#fff",
      icon: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>' }
  };

  function makeChip(variant) {
    var c = chipCfg[variant];
    if (!c) return "";
    return '<span style="display:inline-flex;align-items:center;gap:4px;padding:4px 8px;border-radius:9999px;font-size:13px;font-weight:500;background:' + c.bg + ';color:' + c.color + ';">' + c.icon + ' ' + c.label + '</span>';
  }

  function makeValueRow(label, value) {
    return '<div style="display:flex;flex-direction:column;gap:4px;">' +
      '<span style="font-size:0.8125rem;font-weight:500;color:rgba(38,38,38,0.61);">' + label + '</span>' +
      '<div style="background:#f5f5f5;border-radius:8px;padding:12px;">' +
      '<span style="font-size:0.9375rem;font-weight:500;color:#171717;display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">' + (value || "\\u00A0") + '</span>' +
      '</div></div>';
  }

  function getChipVariants(entry) {
    if (entry.type === "span") return ["modified"];
    if (entry.type === "paragraph") {
      var st = entry.srcStatus || entry.trgStatus;
      if (st === "deleted") return ["deleted"];
      if (st === "inserted") return ["inserted"];
      return ["modified"];
    }
    // active cell 기준으로 chip 결정 (React resolveActiveCell 동일)
    var activeData = activeNavCellId ? CELL_DATA[activeNavCellId] : null;
    var cellSumCaution = activeData ? activeData.sumCaution : entry.sumCaution;
    var cellStatus = activeData ? activeData.status : entry.status;

    var variants = [];
    if (cellSumCaution) variants.push("sumMismatch");
    if (cellStatus === "modified") variants.push("modified");
    else if (cellStatus === "deleted") variants.push("deleted");
    else if (cellStatus === "inserted") variants.push("inserted");

    if (mode === "changes") {
      return variants.filter(function(v) { return v !== "sumMismatch"; });
    }
    if (mode === "sum_diffs" && cellSumCaution) {
      return variants.filter(function(v) { return v === "sumMismatch"; });
    }
    return variants;
  }

  function parseFormattedNumber(value) {
    var trimmed = value.trim();
    var isParenNeg = trimmed.charAt(0) === "(" && trimmed.charAt(trimmed.length - 1) === ")";
    var stripped = isParenNeg ? trimmed.slice(1, -1).replace(/,/g, "") : trimmed.replace(/,/g, "");
    var num = Number(stripped);
    if (isNaN(num)) return null;
    return isParenNeg ? -num : num;
  }

  function formatAccountingNumber(n) {
    return n < 0 ? "(" + Math.abs(n).toLocaleString() + ")" : n.toLocaleString();
  }

  function computeAutoSum(value, diff) {
    if (value == null) return null;
    var parsed = parseFormattedNumber(value);
    if (parsed == null) return null;
    return formatAccountingNumber(parsed + diff);
  }

  function buildNavContent(entry) {
    if (entry.type === "cell") {
      // active cell 결정 (React resolveActiveCell과 동일)
      var isTrgActive = activeNavCellId && entry.cellIds.length > 1 && activeNavCellId === entry.cellIds[entry.cellIds.length - 1];
      var activeData = activeNavCellId ? CELL_DATA[activeNavCellId] : null;
      var activeSumDiff = activeData ? activeData.sumDifference : entry.sumDifference;
      var activeSumCaution = activeData ? activeData.sumCaution : entry.sumCaution;
      var activeValue = isTrgActive ? (entry.trgValue || entry.srcValue) : (entry.srcValue || entry.trgValue);

      var hasSumCaution = mode !== "changes" && activeSumCaution && activeSumDiff != null && activeSumDiff !== 0;
      var diffStr = hasSumCaution ? ((activeSumDiff > 0 ? "+" : "") + activeSumDiff.toLocaleString()) : "";

      if (mode === "sum_diffs" && hasSumCaution && entry.status === "modified") {
        var autoSum = computeAutoSum(activeValue, activeSumDiff);
        var html = makeValueRow("입력한 값", activeValue);
        if (autoSum != null) html += makeValueRow("계산된 합", autoSum);
        html += makeValueRow("단수 차이", diffStr);
        return html;
      }

      if (entry.status === "modified" && entry.srcValue != null && entry.trgValue != null) {
        if (hasSumCaution) {
          var autoSum = computeAutoSum(activeValue, activeSumDiff);
          var html = makeValueRow("기존 값", entry.srcValue);
          if (autoSum != null) html += makeValueRow("계산된 합", autoSum);
          html += makeValueRow("단수 차이", diffStr);
          html += makeValueRow("수정된 값", entry.trgValue);
          return html;
        }
        return makeValueRow("기존 값", entry.srcValue) + makeValueRow("수정된 값", entry.trgValue);
      }
      if (entry.status === "inserted" && hasSumCaution) {
        var autoSum = computeAutoSum(activeValue, activeSumDiff);
        var html = makeValueRow("추가된 값", entry.trgValue || entry.srcValue);
        if (autoSum != null) html += makeValueRow("계산된 합", autoSum);
        html += makeValueRow("단수 차이", diffStr);
        return html;
      }
      if (hasSumCaution) {
        var autoSum = computeAutoSum(activeValue, activeSumDiff);
        var html = makeValueRow("입력한 값", activeValue);
        if (autoSum != null) html += makeValueRow("계산된 합", autoSum);
        html += makeValueRow("단수 차이", diffStr);
        return html;
      }
      var label = entry.status === "deleted" ? "삭제된 값" : entry.status === "inserted" ? "추가된 값" : "값";
      return makeValueRow(label, entry.srcValue || entry.trgValue);
    }
    if (entry.type === "paragraph") {
      var text = entry.srcText || entry.trgText || "";
      return '<div style="background:#f5f5f5;border-radius:8px;padding:12px;font-size:0.9375rem;font-weight:500;color:#171717;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden;">' + text + '</div>';
    }
    var parts = "";
    if (entry.deletedText) parts += makeValueRow("기존", entry.deletedText);
    if (entry.insertedText) parts += makeValueRow("변경", entry.insertedText);
    return parts;
  }

  var chevronLeft = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>';
  var chevronRight = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>';

  function showNavigator(index) {
    if (index < 0 || index >= FILTERED_ENTRIES.length) return;
    currentNavIndex = index;
    var entry = FILTERED_ENTRIES[index];

    var chips = getChipVariants(entry).map(makeChip).join(" ");
    var content = buildNavContent(entry);
    var pos = index + 1;
    var total = FILTERED_ENTRIES.length;

    modalContainer.innerHTML =
      '<div style="display:flex;flex-direction:column;width:320px;background:white;border-radius:12px;border:1px solid rgba(115,115,115,0.22);box-shadow:0px 20px 25px -5px rgba(0,0,0,0.1),0px 4px 24px 4px rgba(0,0,0,0.08);overflow:hidden;" onclick="event.stopPropagation();">' +
        '<div style="display:flex;align-items:center;gap:4px;border-bottom:1px solid rgba(115,115,115,0.22);padding:16px;">' + chips + '</div>' +
        '<div style="display:flex;flex-direction:column;gap:12px;padding:12px 16px;">' + content +
          '<div style="display:flex;align-items:center;justify-content:flex-end;gap:12px;">' +
            '<span style="font-size:0.8125rem;font-weight:500;">' + pos + '<span style="color:rgba(38,38,38,0.61);">/' + total + '</span></span>' +
            '<div style="display:flex;border:1px solid rgba(115,115,115,0.22);overflow:hidden;">' +
              '<button id="dl-nav-prev" type="button" style="display:flex;align-items:center;justify-content:center;padding:12px;background:white;border:none;border-right:1px solid rgba(115,115,115,0.22);cursor:pointer;' + (index === 0 ? 'opacity:0.3;pointer-events:none;' : '') + '">' + chevronLeft + '</button>' +
              '<button id="dl-nav-next" type="button" style="display:flex;align-items:center;justify-content:center;padding:12px;background:white;border:none;cursor:pointer;' + (index === total - 1 ? 'opacity:0.3;pointer-events:none;' : '') + '">' + chevronRight + '</button>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</div>';

    modalContainer.style.display = "block";

    qs("#dl-nav-prev").addEventListener("click", function(e) {
      e.stopPropagation();
      if (currentNavIndex > 0) navigateTo(currentNavIndex - 1);
    });
    qs("#dl-nav-next").addEventListener("click", function(e) {
      e.stopPropagation();
      if (currentNavIndex < FILTERED_ENTRIES.length - 1) navigateTo(currentNavIndex + 1);
    });
  }

  function hideNavigator() {
    modalContainer.style.display = "none";
    modalContainer.innerHTML = "";
    currentNavIndex = -1;
  }

  function navigateTo(index, clickedCellId) {
    clearHighlights();
    var entry = FILTERED_ENTRIES[index];
    if (!entry) return;

    if (entry.type === "cell") {
      var targetId = (clickedCellId && entry.cellIds.indexOf(clickedCellId) >= 0)
        ? clickedCellId
        : entry.cellIds[0];
      activeNavCellId = targetId;
      if (targetId) {
        var el = qs(cellIdSelector(targetId));
        var cellData = CELL_DATA[targetId];
        var selInfo = resolveSelectionInfo(mode, cellData);

        if (el && selInfo) {
          addSelectionOverlay(el, selInfo.color, selInfo.label);
          el.classList.add(DL_SEL_ACTIVE);
          if (cellData && cellData.sumRole === "parent" && !cellData.sumCaution && mode !== "changes") {
            el.classList.add(DL_SUM_MATCH_SEL);
          }
        }

        // sum/sumCaution: pair도 full selection (changes 모드 제외)
        var pairId = (mode !== "changes") ? CELL_PAIR_MAP[targetId] : null;
        if (pairId && cellData && (cellData.sumCaution || cellData.sumRole === "parent")) {
          var pairEl = qs(cellIdSelector(pairId));
          var pairData = CELL_DATA[pairId];
          var pairSelInfo = resolveSelectionInfo(mode, pairData);
          if (pairEl && pairSelInfo) {
            addSelectionOverlay(pairEl, pairSelInfo.color, pairSelInfo.label);
            pairEl.classList.add(DL_SEL_ACTIVE);
            if (pairData && pairData.sumRole === "parent" && !pairData.sumCaution) pairEl.classList.add(DL_SUM_MATCH_SEL);
          }
        }

        // modified 쌍: 양쪽 모두 자기 데이터 기반 overlay + focused 표시
        if (entry.status === "modified" && entry.cellIds.length > 1) {
          entry.cellIds.forEach(function(id) {
            var focusedEl = qs(cellIdSelector(id));
            if (!focusedEl) return;
            focusedEl.classList.add(DL_FOCUSED);
            if (id === targetId) return;
            var focusedData = CELL_DATA[id];
            var focusedSelInfo = resolveSelectionInfo(mode, focusedData);
            if (focusedSelInfo) {
              addSelectionOverlay(focusedEl, focusedSelInfo.color, focusedSelInfo.label);
              focusedEl.classList.add(DL_SEL_ACTIVE);
            }
          });
        }

        // 합계 셀: 자식 하이라이트 (양쪽, 변경 단독 모드 제외)
        highlightSumChildren(targetId);

        // 스크롤 범위: entry cellIds + children + pair children
        var scrollIds = entry.cellIds.slice();
        if (mode !== "changes" && cellData) {
          if (cellData.sumChildren && cellData.sumChildren.length) {
            var lastU = targetId.lastIndexOf("_");
            var prefix = lastU >= 0 ? targetId.substring(0, lastU + 1) : "";
            cellData.sumChildren.forEach(function(addr) { scrollIds.push(prefix + addr); });
          }
          if (pairId) {
            scrollIds.push(pairId);
            var pd = CELL_DATA[pairId];
            if (pd && pd.sumChildren && pd.sumChildren.length) {
              var pu = pairId.lastIndexOf("_");
              var pp = pu >= 0 ? pairId.substring(0, pu + 1) : "";
              pd.sumChildren.forEach(function(addr) { scrollIds.push(pp + addr); });
            }
          }
        }
        scrollCompareCellTargetsIntoView(scrollIds, "smooth");
      }
    } else {
      activeNavCellId = null;
      var changeId = entry.type === "span" ? entry.spanChangeId : entry.paragraphId;
      var els = qsa('[data-change-id="' + escapeDlAttr(changeId) + '"]');
      if (entry.type === "paragraph") {
        // 문단 상태별 ring 색상 (src=첫 번째, trg=두 번째 순서)
        var statusToRing = function(s) {
          if (s === "deleted") return DL_RING_RED;
          if (s === "inserted") return DL_RING_LIME;
          return DL_RING;
        };
        var srcRing = statusToRing(entry.srcStatus);
        var trgRing = statusToRing(entry.trgStatus);
        if (els.length === 1) {
          // 한쪽만 DOM에 존재: srcStatus가 없으면 trg 색상 사용
          els[0].classList.add(entry.srcStatus ? srcRing : trgRing);
        } else {
          els.forEach(function(el, idx) { el.classList.add(idx === 0 ? srcRing : trgRing); });
        }
      } else {
        els.forEach(function(el) { el.classList.add(DL_RING); });
      }
      if (els.length > 0) {
        els[0].scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      }
    }

    showNavigator(index);
  }

  // 네비게이터 열림 시 ←/→ 로 이전·다음 (React ChangeNavigatorPanel과 동일)
  document.addEventListener("keydown", function(e) {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    if (currentNavIndex < 0 || modalContainer.style.display === "none") return;
    var t = e.target;
    if (t && t.nodeType === 1 && typeof t.closest === "function") {
      if (t.closest("input, textarea, select, [contenteditable='true']")) return;
    }
    if (e.key === "ArrowLeft") {
      if (currentNavIndex <= 0) return;
      e.preventDefault();
      navigateTo(currentNavIndex - 1);
      return;
    }
    if (currentNavIndex >= FILTERED_ENTRIES.length - 1) return;
    e.preventDefault();
    navigateTo(currentNavIndex + 1);
  });

  // ═══════════════════════════════════════════
  // Click handler: cell / paragraph / span
  // ═══════════════════════════════════════════

  document.addEventListener("click", function(e) {
    var target = e.target;
    if (!target || typeof target.closest !== "function") return;
    if (target.closest("#dl-navigator")) return;

    // span change
    var spanEl = target.closest("[data-change-id^='span_']");
    if (spanEl) {
      var spanId = spanEl.getAttribute("data-change-id");
      var si = spanIdToIdx[spanId];
      if (si != null) { navigateTo(si); return; }
    }

    // paragraph change
    var paraEl = target.closest("[data-change-id^='para_']");
    if (paraEl) {
      var paraId = paraEl.getAttribute("data-change-id");
      var pi = paraIdToIdx[paraId];
      if (pi != null) { navigateTo(pi); return; }
    }

    // cell (data-cell-id가 있는 td/th)
    var cellEl = target.closest("[data-cell-id]");
    if (cellEl) {
      var cellId = cellEl.getAttribute("data-cell-id");
      var cellData = CELL_DATA[cellId];

      // ghost 셀 무시
      if (cellData && cellData.isGhost) { clearHighlights(); hideNavigator(); return; }

      // 1. change entry에 있으면 → navigateTo
      var ci = cellIdToIdx[cellId];
      if (ci != null) { navigateTo(ci, cellId); return; }

      // 2. sumRole=parent 또는 sumCaution (change entry에 없지만): selection + pair + children
      // changes 모드에서는 합계 인터랙션 생략 → 일반 셀(plain)로 처리
      if (mode !== "changes" && cellData && (cellData.sumRole === "parent" || cellData.sumCaution)) {
        clearHighlights();
        hideNavigator();
        var selInfo = resolveSelectionInfo(mode, cellData);
        if (selInfo) {
          addSelectionOverlay(cellEl, selInfo.color, selInfo.label);
          cellEl.classList.add(DL_SEL_ACTIVE);
          if (cellData.sumRole === "parent" && !cellData.sumCaution && mode !== "changes") cellEl.classList.add(DL_SUM_MATCH_SEL);
        }
        // pair full selection
        var pairId = (mode !== "changes") ? CELL_PAIR_MAP[cellId] : null;
        if (pairId) {
          var pairEl = qs(cellIdSelector(pairId));
          var pairData = CELL_DATA[pairId];
          var pairSelInfo = resolveSelectionInfo(mode, pairData);
          if (pairEl && pairSelInfo) {
            addSelectionOverlay(pairEl, pairSelInfo.color, pairSelInfo.label);
            pairEl.classList.add(DL_SEL_ACTIVE);
            if (pairData && pairData.sumRole === "parent" && !pairData.sumCaution) pairEl.classList.add(DL_SUM_MATCH_SEL);
          }
        }
        highlightSumChildren(cellId);
        var scrollSumIds = [cellId];
        if (mode !== "changes") {
          if (cellData.sumChildren && cellData.sumChildren.length) {
            var su = cellId.lastIndexOf("_");
            var pfx = su >= 0 ? cellId.substring(0, su + 1) : "";
            cellData.sumChildren.forEach(function(addr) { scrollSumIds.push(pfx + addr); });
          }
          if (pairId) {
            scrollSumIds.push(pairId);
            var pd = CELL_DATA[pairId];
            if (pd && pd.sumChildren && pd.sumChildren.length) {
              var pu = pairId.lastIndexOf("_");
              var pp = pu >= 0 ? pairId.substring(0, pu + 1) : "";
              pd.sumChildren.forEach(function(addr) { scrollSumIds.push(pp + addr); });
            }
          }
        }
        scrollCompareCellTargetsIntoView(scrollSumIds, "smooth");
        return;
      }

      // 3. 일반 셀 → plain selection (자신 + pair에 plain border)
      clearHighlights();
      hideNavigator();
      var plainSelInfo = resolveSelectionInfo(mode, cellData);
      var borderColor = plainSelInfo ? SELECTION_COLORS[plainSelInfo.color].border : DEFAULT_PLAIN_BORDER;
      addPlainBorder(cellEl, borderColor);
      var plainPairId = CELL_PAIR_MAP[cellId];
      if (plainPairId) {
        var plainPairEl = qs(cellIdSelector(plainPairId));
        if (plainPairEl) addPlainBorder(plainPairEl, borderColor);
      }
      scrollCompareCellTargetsIntoView(
        plainPairId ? [cellId, plainPairId] : [cellId],
        "smooth"
      );
      return;
    }

    // 빈 영역 클릭 → 닫기
    clearHighlights();
    hideNavigator();
  });

  // ═══════════════════════════════════════════
  // TableHorizontalNavigator — scroll + EdgeShade show/hide 복원
  // React의 조건부 렌더 + onClick이 소실되므로 vanilla JS로 전부 재구현
  // ═══════════════════════════════════════════
  qsa(".overflow-x-auto").forEach(function(scrollEl) {
    var wrapper = scrollEl.parentElement;
    if (!wrapper || !wrapper.classList.contains("relative")) return;

    // EdgeShade 찾기: absolute 자식 중 left-0 / right-0
    var leftShade = null;
    var rightShade = null;
    qsa(":scope > .absolute", wrapper).forEach(function(shade) {
      if (shade.classList.contains("left-0")) leftShade = shade;
      if (shade.classList.contains("right-0")) rightShade = shade;
    });

    // React가 조건부 렌더로 한쪽만 생성했을 수 있으므로, 없는 쪽은 clone해서 생성
    var templateShade = leftShade || rightShade;
    if (!templateShade) return;

    // 다운로드 HTML에서는 더보기 버튼의 SVG 아이콘 제거
    [leftShade, rightShade].forEach(function(s) {
      if (!s) return;
      var svg = s.querySelector("button svg");
      if (svg) svg.remove();
    });

    if (!leftShade && rightShade) {
      leftShade = rightShade.cloneNode(true);
      // right → left 전환
      leftShade.classList.remove("right-0", "justify-end", "pr-6");
      leftShade.classList.add("left-0", "justify-start", "pl-6");
      // mask-image 방향 반전 (to-left → to-right)
      var mask = leftShade.querySelector("[aria-hidden]");
      if (mask && mask.style) {
        var m = mask.style.maskImage || mask.style.webkitMaskImage || "";
        if (m) {
          var flipped = m.replace(/to left/g, "to right");
          mask.style.maskImage = flipped;
          mask.style.webkitMaskImage = flipped;
        }
      }
      wrapper.appendChild(leftShade);
    }
    if (!rightShade && leftShade) {
      rightShade = leftShade.cloneNode(true);
      rightShade.classList.remove("left-0", "justify-start", "pl-6");
      rightShade.classList.add("right-0", "justify-end", "pr-6");
      var mask = rightShade.querySelector("[aria-hidden]");
      if (mask && mask.style) {
        var m = mask.style.maskImage || mask.style.webkitMaskImage || "";
        if (m) {
          var flipped = m.replace(/to right/g, "to left");
          mask.style.maskImage = flipped;
          mask.style.webkitMaskImage = flipped;
        }
      }
      wrapper.appendChild(rightShade);
    }

    function updateShadeVisibility() {
      var sl = scrollEl.scrollLeft;
      var sw = scrollEl.scrollWidth;
      var cw = scrollEl.clientWidth;
      if (leftShade) leftShade.style.display = (sl > 1) ? "" : "none";
      if (rightShade) rightShade.style.display = (sl + cw < sw - 1) ? "" : "none";
    }

    scrollEl.addEventListener("scroll", updateShadeVisibility, { passive: true });
    updateShadeVisibility();

    // 버튼 클릭 → scrollBy
    [leftShade, rightShade].forEach(function(shade) {
      if (!shade) return;
      var btn = shade.querySelector("button");
      if (!btn) return;
      var isLeft = shade.classList.contains("left-0");
      btn.addEventListener("click", function(e) {
        e.stopPropagation();
        scrollEl.scrollBy({ left: isLeft ? -300 : 300, behavior: "smooth" });
      });
    });
  });

  // ═══════════════════════════════════════════
  // Init
  // ═══════════════════════════════════════════
  applyFilter();
})();
`;

// ============================================
// Helper: 로고/파비콘 fetch → data URI
// ============================================

async function fetchLogoAsDataUri(): Promise<string | null> {
  try {
    const response = await fetch("/login-assets/invector-logo.svg");
    const svgText = await response.text();
    return `data:image/svg+xml;base64,${btoa(svgText)}`;
  } catch {
    return null;
  }
}

async function fetchFaviconAsDataUri(): Promise<string | null> {
  try {
    const response = await fetch("/icon-16.png");
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// ============================================
// CSS 추출
// ============================================

function extractAllStyles(): string {
  const cssTexts: string[] = [];
  for (const sheet of document.styleSheets) {
    try {
      for (const rule of sheet.cssRules) {
        cssTexts.push(rule.cssText);
      }
    } catch {
      // cross-origin stylesheet — skip
    }
  }
  return cssTexts.join("\n");
}

// ============================================
// 아이템 필터 판별 유틸
// ============================================

function isItemChanged(item: DsdDsdCompareItem): boolean {
  return (
    item.kind !== "matched" ||
    (item.diffCounts !== null && !item.diffCounts.isIdentical)
  );
}

function itemHasSumCaution(item: DsdDsdCompareItem): boolean {
  const elements = [item.srcElement, item.trgElement, item.element].filter(
    Boolean,
  );
  return elements.some(
    (el) =>
      el &&
      "rows" in el &&
      el.rows.some((row) => row.cells.some((cell) => cell.sumCaution)),
  );
}

// ============================================
// ChangeEntry 직렬화
// ============================================

interface DlCellData {
  sumChildren: string[];
  sumRole: string | null;
  status: string;
  sumCaution: boolean;
  sumDifference: number | null;
  isGhost: boolean;
  isHeader: boolean;
}

/** cellId → 셀 메타데이터 맵 (선택/인터랙션/인디케이터용) */
function buildCellDataMap(
  result: DsdDsdCompareResponse,
): Record<string, DlCellData> {
  const map: Record<string, DlCellData> = {};
  for (const item of result.items) {
    for (const element of [item.srcElement, item.trgElement, item.element]) {
      if (element && "rows" in element) {
        for (const row of element.rows) {
          for (const cell of row.cells) {
            map[cell.cellId] = {
              sumChildren: cell.sumChildren,
              sumRole: cell.sumRole,
              status: cell.status,
              sumCaution: cell.sumCaution,
              sumDifference: cell.sumDifference,
              isGhost: cell.isGhost,
              isHeader: cell.isHeader,
            };
          }
        }
      }
    }
  }
  return map;
}

/** src↔trg cellId 양방향 매핑 (address 기준, matched 테이블만) */
function buildCellPairMap(
  result: DsdDsdCompareResponse,
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const item of result.items) {
    if (item.kind !== "matched") continue;
    const src = item.srcElement;
    const trg = item.trgElement;
    if (!src || !trg || !("rows" in src) || !("rows" in trg)) continue;
    const trgByAddr = new Map<string, string>();
    for (const row of trg.rows)
      for (const cell of row.cells)
        if (!cell.isGhost) trgByAddr.set(cell.address, cell.cellId);
    for (const row of src.rows)
      for (const cell of row.cells) {
        if (cell.isGhost) continue;
        const trgId = trgByAddr.get(cell.address);
        if (trgId) {
          map[cell.cellId] = trgId;
          map[trgId] = cell.cellId;
        }
      }
  }
  return map;
}

function serializeEntries(entries: ChangeEntry[]): object[] {
  return entries.map((entry) => {
    if (entry.type === "cell") {
      const e = entry as CellChangeEntry;
      const src = e.srcCell;
      const trg = e.trgCell;
      const cell = src ?? trg;
      return {
        type: "cell",
        cellIds: e.cellIds,
        status: cell?.status ?? "equal",
        sumCaution: (src?.sumCaution || trg?.sumCaution) ?? false,
        sumDifference: cell?.sumDifference ?? null,
        srcValue: src?.value ?? null,
        trgValue: trg?.value ?? null,
        sectionName: e.sectionName,
      };
    }
    if (entry.type === "paragraph") {
      const e = entry as ParagraphChangeEntry;
      return {
        type: "paragraph",
        paragraphId: e.paragraphId,
        srcText: e.srcParagraph?.text ?? null,
        trgText: e.trgParagraph?.text ?? null,
        srcStatus: e.srcParagraph?.status ?? null,
        trgStatus: e.trgParagraph?.status ?? null,
        sectionName: e.sectionName,
      };
    }
    const e = entry as SpanChangeEntry;
    return {
      type: "span",
      spanChangeId: e.spanChangeId,
      deletedText: e.deletedText,
      insertedText: e.insertedText,
      sectionName: e.sectionName,
    };
  });
}

// ============================================
// 메인 다운로드 함수
// ============================================

/**
 * 드래그 선택 계약(useTableCellSelection) 중 다운로드 산출물에 소비자가 없는 속성.
 * `data-cell-id` 는 남긴다 — 다운로드 HTML 의 셀 클릭 핸들러가 셀을 찾는 열쇠다.
 * `id`(dsd-compare-cell-*)와 `data-display-value` 는 라이브 화면의 선택/복사 전용이라,
 * 정적 산출물에서는 동작하지 않는 채 용량만 차지한다.
 */
const stripLiveOnlySelectionAttrs = (clone: HTMLElement) => {
  clone
    .querySelectorAll("td[data-cell-id][id], th[data-cell-id][id]")
    .forEach((cell) => {
      cell.removeAttribute("id");
      cell.removeAttribute("data-display-value");
    });
};

export const cloneCompareContentForDownload = (contentElement: HTMLElement) => {
  const clone = contentElement.cloneNode(true) as HTMLElement;
  clone
    .querySelectorAll("[data-sticky-header-layer]")
    .forEach((element) => element.remove());
  stripLiveOnlySelectionAttrs(clone);
  return clone;
};

export async function downloadCompareHtml(
  contentElement: HTMLElement,
  result: DsdDsdCompareResponse,
  currentMode: string = "default",
  /** DOM에 렌더된 아이템 (필터 적용 후). paragraph/span ID 인덱스가 DOM과 일치해야 함 */
  filteredItems?: DsdDsdCompareItem[],
  filenames?: { src: string; trg: string },
) {
  const clone = cloneCompareContentForDownload(contentElement);

  // ── React ChangeNavigatorPanel 제거 (JS로 재생성) ──
  // navigatorIndex != null일 때만 렌더되는 SidebarProvider의 직계 자식 fixed div
  for (const child of Array.from(clone.children)) {
    if (
      child instanceof HTMLElement &&
      child.classList.contains("fixed") &&
      !child.querySelector("[data-sidebar]") &&
      !child.hasAttribute("data-state")
    ) {
      child.remove();
    }
  }
  // ring 상태 제거 (paragraph/span 선택 시 ring-2 ring-*-500)
  clone.querySelectorAll("[class*='ring-2']").forEach((el) => {
    el.className = el.className
      .replace(/ring-2\s*/g, "")
      .replace(/ring-\S+\s*/g, "");
  });

  // ── 보고서명 행: DOM 클론 대신 직접 렌더 (absolute overlay 잘림 방지) ──
  clone
    .querySelectorAll(".absolute.top-full.overflow-hidden")
    .forEach((el) => el.remove());
  if (filenames) {
    const fnBar = document.createElement("div");
    fnBar.setAttribute("data-dl-filename-bar", "true");
    fnBar.innerHTML =
      `<div style="display:flex;align-items:center;gap:24px;padding:8px 20px;border-bottom:1px solid var(--line-normal, #e5e5e5);background:var(--background-gray-normal, #fafafa)">` +
      `<div style="flex:1;min-width:0;display:flex;align-items:center;justify-content:center;padding:4px 12px">` +
      `<span style="font-weight:600;font-size:14px;line-height:20px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;color:var(--label-normal, #171717)">${filenames.src}</span></div>` +
      `<div style="flex:1;min-width:0;display:flex;align-items:center;justify-content:center;padding:4px 12px">` +
      `<span style="font-weight:600;font-size:14px;line-height:20px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;color:var(--label-normal, #171717)">${filenames.trg}</span></div>` +
      `</div>`;
    const headerRow = clone.querySelector("[data-dl-compare-header]");
    if (headerRow) {
      headerRow.appendChild(fnBar);
    }
  }

  // ── SVG xmlns 보장 ──
  clone.querySelectorAll("svg").forEach((svg) => {
    if (!svg.getAttribute("xmlns")) {
      svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    }
  });

  // ── 사이드바 강제 표시 (md: 미디어쿼리 의존 제거) ──
  const sidebarWrapper = clone.querySelector(
    "[data-state][data-collapsible]",
  ) as HTMLElement | null;
  if (sidebarWrapper) {
    sidebarWrapper.classList.remove("hidden");
    sidebarWrapper.classList.add("block");
    sidebarWrapper.setAttribute("data-state", "expanded");
    sidebarWrapper.setAttribute("data-collapsible", "");
  }
  clone.querySelectorAll("[data-sidebar='sidebar']").forEach((el) => {
    const parent = el.parentElement;
    if (parent) {
      parent.classList.remove("hidden");
      parent.classList.add("flex");
    }
  });

  // ── SidebarProvider min-h-svh → height: 100svh ──
  // querySelectorAll은 자기 자신을 포함하지 않으므로 root도 별도 처리
  if (clone.classList.contains("min-h-svh")) {
    clone.classList.remove("min-h-svh");
    clone.style.height = "100svh";
  }
  clone.querySelectorAll(".min-h-svh").forEach((el) => {
    el.classList.remove("min-h-svh");
    (el as HTMLElement).style.height = "100svh";
  });

  // ── flex 컨테이너 스크롤 보장 ──
  clone.querySelectorAll("main.flex-1").forEach((el) => {
    (el as HTMLElement).style.minHeight = "0";
    (el as HTMLElement).style.overflow = "hidden";
  });
  clone.querySelectorAll(".flex.flex-col.h-full").forEach((el) => {
    (el as HTMLElement).style.minHeight = "0";
  });

  // ── 모달/오버레이 제거 (사이드바 관련 fixed는 유지) ──
  clone.querySelectorAll(".fixed").forEach((el) => {
    if (!el.querySelector("[data-sidebar]") && !el.closest("[data-state]")) {
      el.remove();
    }
  });

  // ── 로딩 오버레이 제거 ──
  clone.querySelectorAll(".animate-spin").forEach((el) => {
    el.closest(".absolute")?.remove();
  });

  // ── 다운로드 버튼 제거 ──
  clone.querySelectorAll("button").forEach((btn) => {
    if (
      btn.textContent?.includes("다운로드") ||
      btn.textContent?.includes("다운로드 중")
    ) {
      // 부모 div (flex items-center gap-2 px-4 py-3) 제거
      const wrapper = btn.closest(".flex.items-center.gap-2");
      if (wrapper && wrapper.querySelector("button")) {
        wrapper.remove();
      }
    }
  });

  // ── 로고 이미지를 data URI로 교체 ──
  const logoDataUri = await fetchLogoAsDataUri();
  if (logoDataUri) {
    clone.querySelectorAll('img[src*="invector-logo"]').forEach((img) => {
      img.setAttribute("src", logoDataUri);
    });
  }

  // ── 스크롤 컨테이너에 data attribute 추가 ──
  const scrollContainer = clone.querySelector(
    ".flex-1.overflow-y-auto",
  ) as HTMLElement | null;
  if (scrollContainer) {
    scrollContainer.setAttribute("data-scroll-root", "true");
  }

  // ── 세그먼트 탭에 data-dl-filter 속성 추가 ──
  clone.querySelectorAll("button").forEach((btn) => {
    const text = btn.textContent?.trim() || "";
    if (text === "전체") {
      btn.setAttribute("data-dl-filter", "default");
    } else if (text.startsWith("합계 불일치")) {
      btn.setAttribute("data-dl-filter", "sum_diffs");
    } else if (text.startsWith("변경 사항")) {
      btn.setAttribute("data-dl-filter", "changes");
    }
  });

  // ── 각 섹션 그룹 + 아이템 카드에 data attribute 추가 ──
  // DOM에는 filteredItems만 렌더되므로 filteredItems 기준으로 매핑
  const renderedItems = filteredItems ?? result.items;
  let globalIdx = 0;
  const sectionGroups = clone.querySelectorAll("[id^='section-']");
  sectionGroups.forEach((sectionGroup) => {
    (sectionGroup as HTMLElement).setAttribute("data-dl-section-group", "true");
    const cards = sectionGroup.children;
    for (let i = 0; i < cards.length; i++) {
      const card = cards[i] as HTMLElement;
      if (globalIdx < renderedItems.length) {
        const item = renderedItems[globalIdx];
        card.setAttribute("data-dl-item", String(globalIdx));
        card.setAttribute("data-dl-has-changes", String(isItemChanged(item)));
        card.setAttribute(
          "data-dl-has-sum-caution",
          String(itemHasSumCaution(item)),
        );
        globalIdx++;
      }
    }
  });

  // ── 사이드바: React가 적용한 active 스타일 초기화 (JS에서 재계산) ──
  const sidebarElClone = clone.querySelector("[data-sidebar='sidebar']");
  if (sidebarElClone) {
    sidebarElClone.querySelectorAll("button").forEach((btn) => {
      // React가 활성 섹션에 적용한 bg-primary-base text-static-white 제거
      (btn as HTMLElement).classList.remove(
        "bg-primary-base",
        "text-static-white",
      );
    });
  }

  // ── EmptyDiffPlaceholder inline style fallback ──
  // Tailwind arbitrary value 클래스(min-h-[160px] 등)가 추출 CSS에서 누락될 수 있으므로 보장
  clone.querySelectorAll(".border-dashed").forEach((el) => {
    const htmlEl = el as HTMLElement;
    if (!htmlEl.classList.contains("border-dashed")) return;
    htmlEl.style.minHeight = "160px";
    htmlEl.style.display = "flex";
    htmlEl.style.flexDirection = "column";
    htmlEl.style.alignItems = "center";
    htmlEl.style.justifyContent = "center";
    htmlEl.style.gap = "10px";
    htmlEl.style.height = "100%";
    htmlEl.style.borderRadius = "6px";
    htmlEl.style.borderStyle = "dashed";
    htmlEl.style.borderWidth = "1px";
    htmlEl.style.borderColor = "rgba(115, 115, 115, 0.22)";
    htmlEl.style.background = "#f5f5f5";
  });

  // ── 셀 corner radius arbitrary value inline style 보장 ──
  const cornerRadiusMap: Record<string, string> = {
    "rounded-tl-[6px]": "borderTopLeftRadius",
    "rounded-tr-[6px]": "borderTopRightRadius",
    "rounded-bl-[6px]": "borderBottomLeftRadius",
    "rounded-br-[6px]": "borderBottomRightRadius",
  };
  for (const [cls, prop] of Object.entries(cornerRadiusMap)) {
    clone.querySelectorAll(`.${CSS.escape(cls)}`).forEach((el) => {
      (el as HTMLElement).style[prop as any] = "6px";
    });
  }

  // ── Cell Indicator div 주입 ──
  // React가 이미 렌더한 CellIndicator(absolute left-0 w-1) 제거 후 재주입
  clone.querySelectorAll(".absolute.left-0.w-1").forEach((el) => {
    const parent = el.parentElement;
    if (
      parent?.tagName === "TD" ||
      parent?.tagName === "TH" ||
      parent?.closest("td") ||
      parent?.closest("th")
    ) {
      el.remove();
    }
  });

  // 셀 data 속성 마킹 + indicator 주입 (TH 포함)
  const hasChange = (status: string) =>
    status === "deleted" || status === "inserted" || status === "modified";

  for (const item of result.items) {
    for (const element of [item.srcElement, item.trgElement, item.element]) {
      if (element && "rows" in element) {
        for (const row of element.rows) {
          for (const cell of row.cells) {
            const cellEl = clone.querySelector(
              `[data-cell-id="${cell.cellId}"]`,
            ) as HTMLElement | null;
            if (!cellEl) continue;

            cellEl.setAttribute("data-dl-status", cell.status);

            if (hasChange(cell.status)) {
              cellEl.setAttribute("data-dl-has-change", "true");
              // 합계불일치 모드에서 변경색을 걷어낼 때 되돌릴 DSD 원본 배경.
              // sum_diffs 모드에서 받은 클론은 원본 inline bg 가 살아있는데,
              // 리셋 규칙의 !important 가 그걸 이기므로 여기서 따로 보관한다.
              if (cellEl.tagName === "TH" && cellEl.style.backgroundColor) {
                cellEl.style.setProperty(
                  "--dl-th-bg",
                  cellEl.style.backgroundColor,
                );
              }
            }
            if (cell.isGhost) {
              cellEl.setAttribute("data-dl-is-ghost", "true");
            }
            if (cell.sumRole === "parent" && !cell.sumCaution) {
              cellEl.setAttribute("data-dl-sum-match", "true");
            }

            // sumCaution 마커 + indicator
            if (cell.sumCaution) {
              cellEl.setAttribute("data-dl-sum-caution", "true");
              if (hasChange(cell.status)) {
                const indicator = document.createElement("div");
                indicator.className = "dl-indicator";
                cellEl.appendChild(indicator);
              }
            }
          }
        }
      }
    }
  }

  // ── 데이터 직렬화 ──
  // DOM의 paragraph/span data-change-id는 filteredItems 인덱스 기반이므로 동일 리스트로 빌드
  const changeEntries = buildChangeList(
    filteredItems ?? result.items,
    currentMode as DsdDsdViewMode,
  );
  const serializedEntries = serializeEntries(changeEntries);
  const cellDataMap = buildCellDataMap(result);
  const cellPairMap = buildCellPairMap(result);

  // ── CSS 추출 + HTML 생성 ──
  const allStyles = extractAllStyles();
  const faviconDataUri = await fetchFaviconAsDataUri();
  const title = `DSD 대사 - ${result.srcFilename} vs ${result.trgFilename}`;

  const EXTRA_CSS = `
/* ── Download highlight styles ── */
.dl-focused { position: relative; z-index: 5; }
.dl-focused::after { content: ""; position: absolute; inset: 0; pointer-events: none; background: rgba(38,38,38,0.0675); }
.dl-ring { outline: 2px solid #6366f1; outline-offset: -1px; border-radius: 2px; }
.dl-ring-red { outline: 2px solid #ef4444; outline-offset: -1px; border-radius: 2px; }
.dl-ring-lime { outline: 2px solid #84cc16; outline-offset: -1px; border-radius: 2px; }
/* Cell Indicator */
.dl-indicator { position: absolute; left: 0; top: 0; bottom: 0; width: 4px; z-index: 1; background: #171717; }

/* ── 선택/하이라이트 시 indicator 숨김 ── */
td.dl-sel-active .dl-indicator,
th.dl-sel-active .dl-indicator { display: none !important; }
td.dl-highlighted .dl-indicator { display: none !important; }

/* 합계 일치 셀 선택: 흰색 bg, 칩 숨김 */
td.dl-sum-match-sel,
td.dl-sum-match-sel > div:first-child { background: #ffffff !important; }
td.dl-sum-match-sel [data-chip-type="sum-match"],
td.dl-sum-match-sel [data-chip-type="sum-diff"] { display: none !important; }

/* ── 모드별 셀 시각 ── */
/* 전체/변경+합계: sumCaution만(변경 없음) → 검은 bg + 흰 텍스트 */
body.dl-mode-default td[data-dl-sum-caution]:not([data-dl-has-change]),
body.dl-mode-sum_diffs_and_changes td[data-dl-sum-caution]:not([data-dl-has-change]) { background: #171717 !important; }
body.dl-mode-default td[data-dl-sum-caution]:not([data-dl-has-change]) > div:first-child,
body.dl-mode-sum_diffs_and_changes td[data-dl-sum-caution]:not([data-dl-has-change]) > div:first-child { color: #ffffff !important; }
/* 전체/변경+합계: sumCaution만 → 칩 light variant */
body.dl-mode-default td[data-dl-sum-caution]:not([data-dl-has-change]) [data-chip-type="sum-diff"].bg-primary-base.text-static-white,
body.dl-mode-sum_diffs_and_changes td[data-dl-sum-caution]:not([data-dl-has-change]) [data-chip-type="sum-diff"].bg-primary-base.text-static-white { background: #e2e8f0 !important; color: #000000 !important; }

/* 합계불일치: sumCaution 무조건 검은 bg */
body.dl-mode-sum_diffs td[data-dl-sum-caution] { background: #171717 !important; }
body.dl-mode-sum_diffs td[data-dl-sum-caution] > div:first-child { color: #ffffff !important; }
/* 합계불일치: 칩 항상 light variant */
body.dl-mode-sum_diffs td[data-dl-sum-caution] [data-chip-type="sum-diff"].bg-primary-base.text-static-white { background: #e2e8f0 !important; color: #000000 !important; }

/* 변경: 합계/단수차이 칩 숨김, indicator 숨김 */
body.dl-mode-changes [data-chip-type="sum-match"],
body.dl-mode-changes [data-chip-type="sum-diff"] { display: none !important; }
body.dl-mode-changes .dl-indicator { display: none !important; }

/* 합계불일치: indicator 숨김 */
body.dl-mode-sum_diffs .dl-indicator { display: none !important; }
/* 합계불일치: 변경 배경색 리셋 (deleted/inserted/modified) */
body.dl-mode-sum_diffs td[data-dl-has-change]:not([data-dl-sum-caution]) { background: transparent !important; }
body.dl-mode-sum_diffs td[data-dl-has-change]:not([data-dl-sum-caution]) > div:first-child { background: transparent !important; color: inherit !important; }
/* 합계불일치: 변경 전용 셀 hover/cursor 제거 */
body.dl-mode-sum_diffs td[data-dl-has-change]:not([data-dl-sum-caution]) > div:first-child { cursor: default !important; }
body.dl-mode-sum_diffs td[data-dl-has-change]:not([data-dl-sum-caution]) > div:first-child:hover,
body.dl-mode-sum_diffs td[data-dl-has-change]:not([data-dl-sum-caution]) > div:first-child:active { background-color: inherit !important; }
body.dl-mode-sum_diffs td[data-dl-has-change]:not([data-dl-sum-caution]) > div:first-child:hover::before,
body.dl-mode-sum_diffs td[data-dl-has-change]:not([data-dl-sum-caution]) > div:first-child:active::before { background-color: transparent !important; }
/* 합계불일치: 헤더(TH) 변경 배경색 리셋 — DSD 원본 배경(없으면 미변경 헤더와 같은 bg-neutral-200) */
body.dl-mode-sum_diffs th[data-dl-has-change] { background-color: var(--dl-th-bg, #e5e5e5) !important; }
body.dl-mode-sum_diffs th[data-dl-has-change] > div { cursor: default !important; }
body.dl-mode-sum_diffs th[data-dl-has-change] > div:hover::before,
body.dl-mode-sum_diffs th[data-dl-has-change] > div:active::before { background-color: transparent !important; }

/* Sum children highlight (bg-orange-200) — 합계불일치 dark bg보다 우선해야 하므로 모드별 규칙 뒤에 배치 */
td.dl-highlighted,
td.dl-highlighted > div:first-child { background: #fed7aa !important; color: #000000 !important; }
/* highlighted 시 단수차이칩 dark variant 강제 (light/dark 모두 대상) */
td.dl-highlighted [data-chip-type="sum-diff"] { background: #171717 !important; color: #ffffff !important; }

/* Sidebar active section */
.dl-sidebar-active { background: #171717 !important; color: #ffffff !important; }

/* ── changes 모드: hover 인터랙션 override ── */
/* 비변경 셀: cursor + hover 제거 */
body.dl-mode-changes td:not([data-dl-has-change]) > div:first-child,
body.dl-mode-changes th:not([data-dl-has-change]) > div { cursor: default !important; }
body.dl-mode-changes td:not([data-dl-has-change]) > div:first-child:hover,
body.dl-mode-changes td:not([data-dl-has-change]) > div:first-child:active { background-color: inherit !important; }
body.dl-mode-changes td:not([data-dl-has-change]) > div:first-child:hover::before,
body.dl-mode-changes td:not([data-dl-has-change]) > div:first-child:active::before { background-color: transparent !important; }
/* 합계 일치 셀: orange hover bg 리셋 */
body.dl-mode-changes td[data-dl-sum-match] > div:first-child:hover,
body.dl-mode-changes td[data-dl-sum-match] > div:first-child:active { background-color: inherit !important; }
/* 합계 불일치 셀: white/strong hover 리셋 + 변경 시 neutral hover 적용 */
body.dl-mode-changes td[data-dl-sum-caution] > div:first-child:hover::before,
body.dl-mode-changes td[data-dl-sum-caution] > div:first-child:active::before { background-color: transparent !important; }
body.dl-mode-changes td[data-dl-sum-caution][data-dl-has-change] > div:first-child { cursor: pointer !important; }
body.dl-mode-changes td[data-dl-sum-caution][data-dl-has-change] > div:first-child:hover::before { background-color: rgba(10,10,10,0.06) !important; }
body.dl-mode-changes td[data-dl-sum-caution][data-dl-has-change] > div:first-child:active::before { background-color: rgba(10,10,10,0.12) !important; }
/* 합계 일치 + 변경: neutral hover 적용 (::before setup 보장) */
body.dl-mode-changes td[data-dl-sum-match][data-dl-has-change] > div:first-child { cursor: pointer !important; position: relative; }
body.dl-mode-changes td[data-dl-sum-match][data-dl-has-change] > div:first-child::before { content: ""; position: absolute; inset: 0; pointer-events: none; transition: background-color 150ms; }
body.dl-mode-changes td[data-dl-sum-match][data-dl-has-change] > div:first-child:hover::before { background-color: rgba(10,10,10,0.06) !important; }
body.dl-mode-changes td[data-dl-sum-match][data-dl-has-change] > div:first-child:active::before { background-color: rgba(10,10,10,0.12) !important; }

/* ── changes 모드: 합계 불일치(변경 없음) dark bg 리셋 ── */
body.dl-mode-changes td[data-dl-sum-caution]:not([data-dl-has-change]) { background: transparent !important; }
body.dl-mode-changes td[data-dl-sum-caution]:not([data-dl-has-change]) > div:first-child { color: inherit !important; background: transparent !important; }

/* ── ghost 셀: 인터랙션 제거 ── */
td[data-dl-is-ghost] > div:first-child,
th[data-dl-is-ghost] > div { cursor: default !important; pointer-events: none !important; }
td[data-dl-is-ghost] > div:first-child::before,
th[data-dl-is-ghost] > div::before { display: none !important; }
`;

  const html = `<!DOCTYPE html>
<html lang="ko">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${title}</title>${faviconDataUri ? `\n  <link rel="icon" type="image/png" href="${faviconDataUri}">` : ""}
  <style>
${FONT_IMPORT}
${allStyles}
${EXTRA_CSS}
html, body { height: 100%; margin: 0; padding: 0; }
  </style>
</head>
<body class="dl-mode-${currentMode}">
  ${clone.outerHTML}
  <script>
    window.__DL_CHANGE_ENTRIES__ = ${JSON.stringify(serializedEntries)};
    window.__DL_SECTIONS__ = ${JSON.stringify(result.sections)};
    window.__DL_CELL_DATA__ = ${JSON.stringify(cellDataMap)};
    window.__DL_CELL_PAIR_MAP__ = ${JSON.stringify(cellPairMap)};
    window.__DL_INITIAL_MODE__ = ${JSON.stringify(currentMode)};
  <\/script>
  <script>${INLINE_SCRIPT}<\/script>
</body>
</html>`;

  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const filename = `DSD대사_(${result.srcFilename})_(${result.trgFilename}).html`;
  downloadBlob(blob, filename);
}
